// @dallali/notifications — shared TypeScript surface for the notification
// dispatcher (events, channels, audiences, dispatch DTOs) plus the Voxa
// (Hatif) WhatsApp delivery channel types and Zod schemas. Imported by both
// the React frontend and the NestJS API.
//
// Built to ./dist via tsc (see package.json `build`/`prepare`). Node consumers
// resolve the compiled JS through `main`/`types` (dist), so the compiled NestJS
// API can `require()` it at runtime and the deploy vendor step ships a real
// `dist/`. Bundlers (Vite / vitest) resolve it from THIS source via a path
// alias, so the FE build never needs dist to exist.
//
// Kept as a SINGLE source file with zero relative imports. This is no longer a
// hard requirement now that there's a dist build, but it stays deliberately
// unsplit: an earlier source-only era (`main` -> this .ts file) crash-looped the
// API when relative VALUE imports (`./x.js` -> x.ts) failed to resolve under
// Node's raw type-stripping (caught by the journeys rig, PR #3024), and one
// file keeps resolution unambiguous for every consumer (FE bundler, tsc, jest's
// src mapper). Split only alongside a resolution test.

import * as z from 'zod';

// ═══════════════════════ (was voxa/channels.ts) ═══════════════════════

// Voxa Channels API — GET /v1/channels/service-account response shape.
// Schemas + inferred types copied verbatim from the official documentation
// Zod export so any drift from the docs is a single-file edit. The package
// barrel re-exports everything here.

export const WhatsAppSessionInfoSchema = z.object({
  id: z.string(),
  lastConnectedAt: z.union([z.coerce.date(), z.null()]).optional(),
  sessionName: z.string(),
  status: z.number(),
});
export type WhatsAppSessionInfo = z.infer<typeof WhatsAppSessionInfoSchema>;

export const BusinessAccountInfoSchema = z.object({
  displayPhoneNumber: z.string(),
  id: z.string(),
  isActive: z.boolean(),
  phoneNumberId: z.string(),
  provider: z.number(),
  qualityRating: z.union([z.null(), z.string()]).optional(),
  status: z.number(),
});
export type BusinessAccountInfo = z.infer<typeof BusinessAccountInfoSchema>;

export const WhatsAppInfoSchema = z.object({
  businessAccountInfo: z
    .union([BusinessAccountInfoSchema, z.null()])
    .optional(),
  providerType: z.number(),
  sessionInfo: z.union([WhatsAppSessionInfoSchema, z.null()]).optional(),
});
export type WhatsAppInfo = z.infer<typeof WhatsAppInfoSchema>;

export const SmartReconnectConfigurationSchema = z.object({
  enabled: z.boolean(),
  fallbackStrategy: z.number(),
  mobileRingTimeoutSeconds: z.number(),
  sipRingTimeoutSeconds: z.number(),
  windowMinutes: z.number(),
});
export type SmartReconnectConfiguration = z.infer<
  typeof SmartReconnectConfigurationSchema
>;

export const PhoneNumberSchema = z.object({
  id: z.string(),
  number: z.string(),
  phoneNumberStatus: z.number(),
  provider: z.union([z.null(), z.string()]).optional(),
  region: z.union([z.null(), z.string()]).optional(),
  workspaceId: z.string(),
});
export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;

export const InboundAgentConfigurationSchema = z.object({
  agentId: z.union([z.null(), z.string()]).optional(),
  enabled: z.boolean(),
  sipRingTimeoutSeconds: z.number(),
});
export type InboundAgentConfiguration = z.infer<
  typeof InboundAgentConfigurationSchema
>;

export const ChannelItemSchema = z.object({
  creationTime: z.coerce.date(),
  creatorId: z.string(),
  evaluationTemplateId: z.union([z.null(), z.string()]).optional(),
  icon: z.string(),
  id: z.string(),
  inboundAgentConfiguration: z
    .union([InboundAgentConfigurationSchema, z.null()])
    .optional(),
  lastModificationTime: z.coerce.date(),
  lastModifierId: z.union([z.null(), z.string()]).optional(),
  name: z.string(),
  phoneNumber: z.union([PhoneNumberSchema, z.null()]).optional(),
  postCallWebhookUrl: z.union([z.null(), z.string()]).optional(),
  smartReconnectConfiguration: SmartReconnectConfigurationSchema,
  type: z.number(),
  whatsAppInfo: z.union([WhatsAppInfoSchema, z.null()]).optional(),
  workspaceId: z.string(),
});
export type ChannelItem = z.infer<typeof ChannelItemSchema>;

export const ChannelListResponseSchema = z.object({
  items: z.array(ChannelItemSchema),
  totalCount: z.number(),
});
export type ChannelListResponse = z.infer<typeof ChannelListResponseSchema>;

// ═══════════════════════ (was voxa/contacts.ts) ═══════════════════════

// Voxa Contacts API — GET /v1/contacts response shape.
//
// Schemas + inferred types follow the same docs-verbatim pattern as
// ./channels.ts. The list endpoint wraps its page under a top-level `id`
// key — unlike every other Voxa list endpoint — so the wrapped shape is
// expressed faithfully here and unwrapped by the client.

export const ContactCustomPropertyValueSchema = z.object({
  propertyDefinitionId: z.string(),
  propertyName: z.string(),
  propertyType: z.number(),
  textValue: z.union([z.null(), z.string()]).optional(),
  numberValue: z.union([z.null(), z.number()]).optional(),
  dateValue: z.union([z.null(), z.string()]).optional(),
  selectValue: z.union([z.null(), z.string()]).optional(),
});
export type ContactCustomPropertyValue = z.infer<
  typeof ContactCustomPropertyValueSchema
>;

export const ContactNoteSchema = z.object({
  id: z.string(),
  text: z.string(),
  creationTime: z.coerce.date(),
  creatorId: z.union([z.null(), z.string()]),
});
export type ContactNote = z.infer<typeof ContactNoteSchema>;

export const ContactSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  phoneNumber: z.string(),
  contactType: z.number(),
  email: z.union([z.null(), z.string()]),
  company: z.union([z.null(), z.string()]),
  position: z.union([z.null(), z.string()]),
  customFields: z.union([z.record(z.unknown()), z.null()]),
  notes: z.array(ContactNoteSchema),
  customProperties: z.array(ContactCustomPropertyValueSchema),
  creationTime: z.coerce.date(),
  creatorId: z.union([z.null(), z.string()]),
  lastModificationTime: z.union([z.coerce.date(), z.null()]),
  lastModifierId: z.union([z.null(), z.string()]),
});
export type Contact = z.infer<typeof ContactSchema>;

// Voxa wraps the list response under `id`. The page itself is a standard
// `{ totalCount, items }` shape — the get-contacts client unwraps it so
// callers see a flat PagedResult-like value.
export const ContactListPageSchema = z.object({
  totalCount: z.number(),
  items: z.array(ContactSchema),
});
export type ContactListPage = z.infer<typeof ContactListPageSchema>;

export const ContactListResponseSchema = z.object({
  id: ContactListPageSchema,
});
export type ContactListResponse = z.infer<typeof ContactListResponseSchema>;

// History row returned by GET /v1/contacts/{id}/history. Unlike
// /v1/contacts, the history endpoint returns the page directly (no `id`
// wrapper).
export const ContactHistoryItemSchema = z.object({
  id: z.string(),
  propertyName: z.string(),
  oldValue: z.union([z.null(), z.string()]),
  newValue: z.union([z.null(), z.string()]),
  creationTime: z.coerce.date(),
  creatorId: z.union([z.null(), z.string()]),
});
export type ContactHistoryItem = z.infer<typeof ContactHistoryItemSchema>;

export const ContactHistoryListSchema = z.object({
  totalCount: z.number(),
  items: z.array(ContactHistoryItemSchema),
});
export type ContactHistoryList = z.infer<typeof ContactHistoryListSchema>;

// ═══════════════════════ (was voxa/templates.ts) ═══════════════════════

// Voxa WhatsApp Templates API — GET
// /v1/whatsapp-business-accounts/{wabaId}/templates response shape.
// Schemas + inferred types mirror the live Voxa payload so the admin
// "Notification templates" page can render the real, Meta-approved content +
// approval status (instead of the seeded display copy, which drifts from Meta).

// Numeric category code Voxa/Meta returns (and we submit). Mirrors
// WHATSAPP_CATEGORY in the API seeders: MARKETING = 1, UTILITY = 2,
// AUTHENTICATION = 3.
export const VoxaTemplateCategory = {
  MARKETING: 1,
  UTILITY: 2,
  AUTHENTICATION: 3,
} as const;
export type VoxaTemplateCategory =
  (typeof VoxaTemplateCategory)[keyof typeof VoxaTemplateCategory];

// Numeric review status Voxa returns. 1 = Active/approved, 2 = In Review,
// 3 = Rejected (verified against the live list — the dashboard labels match).
export const VoxaTemplateStatus = {
  ACTIVE: 1,
  IN_REVIEW: 2,
  REJECTED: 3,
} as const;
export type VoxaTemplateStatus =
  (typeof VoxaTemplateStatus)[keyof typeof VoxaTemplateStatus];

// Numeric component type (Meta): 1 = HEADER, 2 = BODY, 3 = FOOTER, 4 = BUTTONS.
export const VoxaComponentType = {
  HEADER: 1,
  BODY: 2,
  FOOTER: 3,
  BUTTONS: 4,
} as const;

// A button inside a BUTTONS component. `type` 2 = URL (the only kind we use).
export const VoxaTemplateButtonSchema = z.object({
  type: z.number(),
  text: z.string(),
  url: z.union([z.null(), z.string()]).optional(),
  phoneNumber: z.union([z.null(), z.string()]).optional(),
  example: z.union([z.array(z.string()), z.null()]).optional(),
});
export type VoxaTemplateButton = z.infer<typeof VoxaTemplateButtonSchema>;

// Per-component example values. Voxa nests bodyText one level deeper (an array
// of arrays — one row of params), headerText is a flat array.
export const VoxaTemplateExampleSchema = z.object({
  headerText: z.union([z.array(z.string()), z.null()]).optional(),
  bodyText: z.union([z.array(z.array(z.string())), z.null()]).optional(),
  headerHandle: z.union([z.array(z.string()), z.null()]).optional(),
});

export const VoxaTemplateComponentSchema = z.object({
  type: z.number(),
  format: z.union([z.null(), z.string()]).optional(),
  text: z.union([z.null(), z.string()]).optional(),
  buttons: z.union([z.array(VoxaTemplateButtonSchema), z.null()]).optional(),
  example: z.union([VoxaTemplateExampleSchema, z.null()]).optional(),
});
export type VoxaTemplateComponent = z.infer<typeof VoxaTemplateComponentSchema>;

export const VoxaTemplateSchema = z.object({
  id: z.string(),
  templateId: z.union([z.null(), z.string()]).optional(),
  externalId: z.union([z.null(), z.string()]).optional(),
  name: z.string(),
  language: z.string(),
  namespace: z.union([z.null(), z.string()]).optional(),
  // Constrained to the known codes via the VoxaTemplateStatus / VoxaTemplateCategory
  // maps (the single source of truth for what 1/2/3 mean) so an unexpected value
  // is a typed surprise rather than silently mapping to 'in_review' in
  // approvalState(). z.infer types these as the VoxaTemplate{Status,Category} unions.
  status: z.nativeEnum(VoxaTemplateStatus),
  category: z.nativeEnum(VoxaTemplateCategory),
  rejectedReason: z.union([z.null(), z.string()]).optional(),
  qualityScore: z.union([z.null(), z.string()]).optional(),
  components: z.array(VoxaTemplateComponentSchema),
  createdAt: z.union([z.coerce.date(), z.null()]).optional(),
  modifiedAt: z.union([z.coerce.date(), z.null()]).optional(),
});
export type VoxaTemplate = z.infer<typeof VoxaTemplateSchema>;

// The endpoint returns a bare array of templates (no paging envelope).
export const VoxaTemplateListSchema = z.array(VoxaTemplateSchema);
export type VoxaTemplateList = z.infer<typeof VoxaTemplateListSchema>;

// ═══════════════════════ (was voxa/types.ts) ═══════════════════════

// Voxa TypeScript types — shared between the React frontend (via the
// `@dallali/notifications` package) and the NestJS `/hatif/*` controller.
// The route prefix is "hatif" because that is Dallali's internal product
// name for the Voxa integration; the types intentionally keep the `Hatif`
// prefix so the controller route, request DTOs, and the wire shape line up.
//
// Numeric/string enums use the `as const` + keyof typeof pattern so the
// values are usable both as a runtime lookup and as a literal-union type.
//
// Pure types + as-const enums; no runtime side-effects. Body schemas live
// next to this file in `voxa/schemas.ts` and are consumed BE-side via
// `createZodDto` (nestjs-zod) — see apps/api/src/hatif/dto/api-requests.dto.ts.
// ---------------------------------------------------------------------------

export const ConversationStatus = {
  Open: 1,
  Closed: 2,
} as const;
export type ConversationStatus =
  (typeof ConversationStatus)[keyof typeof ConversationStatus];

export const ChannelType = {
  PhoneNumber: 1,
  WhatsApp: 2,
  PhoneNumberAndWhatsapp: 3,
} as const;
export type ChannelType = (typeof ChannelType)[keyof typeof ChannelType];

export const EventSourceType = {
  Call: 1,
  WhatsApp: 2,
  Assignation: 3,
} as const;
export type EventSourceType =
  (typeof EventSourceType)[keyof typeof EventSourceType];

export const EventDirection = {
  Inbound: 1,
  Outbound: 2,
  Internal: 3,
} as const;
export type EventDirection =
  (typeof EventDirection)[keyof typeof EventDirection];

export const WhatsAppTemplateValueType = {
  Text: 'text',
  Image: 'image',
  Document: 'document',
  Video: 'video',
} as const;
export type WhatsAppTemplateValueType =
  (typeof WhatsAppTemplateValueType)[keyof typeof WhatsAppTemplateValueType];

export const WhatsAppTemplateParameterType = {
  Header: 'Header',
  Body: 'Body',
  Buttons: 'Buttons',
  Footer: 'Footer',
} as const;
export type WhatsAppTemplateParameterType =
  (typeof WhatsAppTemplateParameterType)[keyof typeof WhatsAppTemplateParameterType];

export const WhatsAppTemplateButtonSubType = {
  Url: 'url',
  QuickReply: 'quick_reply',
} as const;
export type WhatsAppTemplateButtonSubType =
  (typeof WhatsAppTemplateButtonSubType)[keyof typeof WhatsAppTemplateButtonSubType];

export const WhatsAppTemplateButtonIndex = {
  First: '0',
  Second: '1',
  Third: '2',
} as const;
export type WhatsAppTemplateButtonIndex =
  (typeof WhatsAppTemplateButtonIndex)[keyof typeof WhatsAppTemplateButtonIndex];

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

// Voxa's standard paged response wrapper. Kept as a generic TS interface
// because Zod doesn't model generics ergonomically — callers build a
// concrete `z.object({ totalCount, items: z.array(itemSchema) })` per
// endpoint (see voxa/channels.ts `ChannelListResponseSchema`,
// voxa/contacts.ts `ContactHistoryListSchema`).
export interface PagedResult<T> {
  totalCount: number;
  items: T[];
}

// PagedQuery / SortedPagedQuery — inferred from voxa/schemas.ts.

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

// Channel response schemas + inferred types live in `./channels.js` (the
// shape is the Voxa docs export verbatim). Re-exported by the package barrel.

// Account API
export type HatifAccountTokenResponse = z.infer<
  typeof HatifAccountTokenResponseSchema
>;

// Pagination
export type PagedQuery = z.infer<typeof PagedQuerySchema>;
export type SortedPagedQuery = z.infer<typeof SortedPagedQuerySchema>;

// ---------------------------------------------------------------------------
// Tags — inferred from voxa/schemas.ts
// ---------------------------------------------------------------------------
export type HatifTag = z.infer<typeof HatifTagSchema>;
export type HatifCreateTagRequest = z.infer<typeof CreateTagBodySchema>;
export type HatifUpdateTagRequest = z.infer<typeof UpdateTagBodySchema>;

// ---------------------------------------------------------------------------
// Conversations — inferred from voxa/schemas.ts
// ---------------------------------------------------------------------------
export type HatifChannelConversation = z.infer<
  typeof HatifChannelConversationSchema
>;
export type HatifConversation = z.infer<typeof HatifConversationSchema>;
export type HatifAiSummary = z.infer<typeof HatifAiSummarySchema>;
export type HatifReplyInfo = z.infer<typeof HatifReplyInfoSchema>;
export type HatifInternalThread = z.infer<typeof HatifInternalThreadSchema>;
export type HatifLocationMessage = z.infer<typeof HatifLocationMessageSchema>;
// Voxa's wire shape for a conversation-handoff timeline event. Both "from"
// and "to" sides collapse user-or-AI into one `*AssigneeName` for display.
export type HatifAssignationInfo = z.infer<typeof HatifAssignationInfoSchema>;
export type HatifConversationTimelineEvent = z.infer<
  typeof HatifConversationTimelineEventSchema
>;
export type HatifListConversationsQuery = z.infer<
  typeof ListConversationsQuerySchema
>;
export type HatifCreateConversationRequest = z.infer<
  typeof CreateConversationBodySchema
>;
export type HatifCreateConversationResult = z.infer<
  typeof HatifCreateConversationResultSchema
>;
export type HatifAssignConversationRequest = z.infer<
  typeof AssignConversationBodySchema
>;

// ---------------------------------------------------------------------------
// WhatsApp send — inferred from voxa/schemas.ts
// ---------------------------------------------------------------------------
export type HatifSendWhatsAppTextRequest = z.infer<
  typeof SendWhatsAppTextBodySchema
>;
// Same shape as the template result — Voxa returns the conversation event id
// for both sendText and sendTemplate.
export type HatifSendWhatsAppTextResult = z.infer<
  typeof HatifSendWhatsAppTextResultSchema
>;
export type HatifWhatsAppTemplateValue = z.infer<
  typeof WhatsAppTemplateValueBodySchema
>;
export type HatifWhatsAppTemplateParameter = z.infer<
  typeof WhatsAppTemplateParameterBodySchema
>;
export type HatifSendWhatsAppTemplateRequest = z.infer<
  typeof SendWhatsAppTemplateBodySchema
>;
export type HatifSendWhatsAppTemplateResult = z.infer<
  typeof HatifSendWhatsAppTemplateResultSchema
>;

// ---------------------------------------------------------------------------
// Contact (subset) — response schemas + inferred types live in
// `./contacts.js`. Request-body types are inferred from voxa/schemas.ts.
// ---------------------------------------------------------------------------
export type HatifContactSearchFilter = z.infer<
  typeof ContactSearchFilterBodySchema
>;
export type HatifSearchContactsRequest = z.infer<
  typeof SearchContactsComplexBodySchema
>;
export type HatifSetContactPropertyValueRequest = z.infer<
  typeof SetContactPropertyValueBodySchema
>;

// ---------------------------------------------------------------------------
// Voxa enums — BE-side additions (not currently used by the FE messaging UI,
// but kept here so the surface is shared).
// ---------------------------------------------------------------------------

export const CallSentiment = {
  Positive: 1,
  Neutral: 2,
  Negative: 3,
  Mixed: 4,
  Unknown: 5,
} as const;
export type CallSentiment = (typeof CallSentiment)[keyof typeof CallSentiment];

export const CallStatus = {
  Active: 0,
  Completed: 1,
  Missed: 2,
  RejectedByCaller: 3,
  RejectedByCallee: 4,
  NoAnswer: 5,
  Cancelled: 6,
  Failed: 7,
  Ringing: 8,
} as const;
export type CallStatus = (typeof CallStatus)[keyof typeof CallStatus];

export const CallType = {
  Inbound: 1,
  Outbound: 2,
} as const;
export type CallType = (typeof CallType)[keyof typeof CallType];

export const WorkspaceUserRole = {
  Owner: 1,
  Member: 2,
} as const;
export type WorkspaceUserRole =
  (typeof WorkspaceUserRole)[keyof typeof WorkspaceUserRole];

export const IvrTtsVoice = {
  Male: 'Male',
  Female: 'Female',
} as const;
export type IvrTtsVoice = (typeof IvrTtsVoice)[keyof typeof IvrTtsVoice];

export const WhatsAppDirection = {
  Inbound: 'Inbound',
  Outbound: 'Outbound',
} as const;
export type WhatsAppDirection =
  (typeof WhatsAppDirection)[keyof typeof WhatsAppDirection];

export const WhatsAppMessageType = {
  Text: 'Text',
  Image: 'Image',
  Video: 'Video',
  Audio: 'Audio',
  Document: 'Document',
  Location: 'Location',
  Contact: 'Contact',
  Sticker: 'Sticker',
  Template: 'Template',
  Interactive: 'Interactive',
} as const;
export type WhatsAppMessageType =
  (typeof WhatsAppMessageType)[keyof typeof WhatsAppMessageType];

export const WhatsAppMessageStatus = {
  Sent: 'Sent',
  Delivered: 'Delivered',
  Read: 'Read',
  Pending: 'Pending',
  Failed: 'Failed',
} as const;
export type WhatsAppMessageStatus =
  (typeof WhatsAppMessageStatus)[keyof typeof WhatsAppMessageStatus];

export const OutboundIvrStatus = {
  Pending: 'Pending',
  InProgress: 'InProgress',
  Completed: 'Completed',
  Failed: 'Failed',
} as const;
export type OutboundIvrStatus =
  (typeof OutboundIvrStatus)[keyof typeof OutboundIvrStatus];

export const OutboundIvrResult = {
  Success: 'Success',
  NoAnswer: 'NoAnswer',
  Busy: 'Busy',
  Failed: 'Failed',
  DtmfTimeout: 'DtmfTimeout',
} as const;
export type OutboundIvrResult =
  (typeof OutboundIvrResult)[keyof typeof OutboundIvrResult];

// ═══════════════════════ (was voxa/schemas.ts) ═══════════════════════

// Zod schemas for the /hatif/* controller body + query DTOs.
//
// Used on the BE via `createZodDto(...)` (nestjs-zod) so the global
// ValidationPipe enforces them — replaces the old class-validator
// decorations. The FE imports the *types* (via `z.infer<typeof X>`) so the
// request payloads are typed consistently with the runtime guard.
//
// Voxa IDs look UUID-shaped but are NOT RFC-4122 (version nibble + variant
// don't follow the spec), so we validate the hex-block shape only.

export const VOXA_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const VoxaId = z
  .string()
  .regex(VOXA_ID_RE, 'must be a Voxa id (hex 8-4-4-4-12)');

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const PagedQuerySchema = z.object({
  SkipCount: z.coerce.number().int().min(0).optional(),
  MaxResultCount: z.coerce.number().int().min(1).max(200).optional(),
});

export const SortedPagedQuerySchema = PagedQuerySchema.extend({
  Sorting: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export const ListConversationsQuerySchema = z.object({
  Status: z.coerce.number().pipe(z.nativeEnum(ConversationStatus)).optional(),
  AssigneeUserIds: z.string().optional(),
  GetUnAssigned: z.coerce.boolean().optional(),
  ContactIds: z.string().optional(),
  TagIds: z.string().optional(),
  Name: z.string().optional(),
  PhoneNumber: z.string().optional(),
  FromDate: z.string().optional(),
  ToDate: z.string().optional(),
  IsLost: z.coerce.boolean().optional(),
  Sorting: z.string().optional(),
  SkipCount: z.coerce.number().int().min(0).optional(),
  MaxResultCount: z.coerce.number().int().min(1).max(200).optional(),
});

export const CreateConversationBodySchema = z.object({
  ChannelId: VoxaId,
  PhoneNumber: z.string(),
  ContactName: z.string().nullish(),
  AssignToUserId: VoxaId.nullish(),
  AssignToAiAgentId: VoxaId.nullish(),
});

export const AssignConversationBodySchema = z.object({
  AssignedUserId: VoxaId.nullish(),
  AssignedAiAgentId: VoxaId.nullish(),
});

// ---------------------------------------------------------------------------
// WhatsApp send
// ---------------------------------------------------------------------------

export const SendWhatsAppTextBodySchema = z.object({
  ChannelId: VoxaId,
  ToNumber: z.string(),
  Text: z.string().min(1).max(4096),
});

export const WhatsAppTemplateValueBodySchema = z.object({
  Type: z.nativeEnum(WhatsAppTemplateValueType),
  Text: z.string().optional(),
  ImageUrl: z.string().optional(),
  DocumentUrl: z.string().optional(),
  DocumentFilename: z.string().optional(),
  VideoUrl: z.string().optional(),
});

export const WhatsAppTemplateParameterBodySchema = z.object({
  Type: z.nativeEnum(WhatsAppTemplateParameterType),
  Values: z.array(WhatsAppTemplateValueBodySchema),
  SubType: z.nativeEnum(WhatsAppTemplateButtonSubType).optional(),
  Index: z.nativeEnum(WhatsAppTemplateButtonIndex).optional(),
});

export const SendWhatsAppTemplateBodySchema = z.object({
  ChannelId: VoxaId,
  TemplateName: z.string(),
  Language: z.string().min(2).max(5),
  ToNumber: z.string(),
  Parameters: z.array(WhatsAppTemplateParameterBodySchema),
});

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export const CreateTagBodySchema = z.object({
  Name: z.string().min(1).max(100),
  Icon: z.string().nullish(),
  Description: z.string().nullish(),
  IsPinned: z.boolean().nullish(),
});

export const UpdateTagBodySchema = z.object({
  Name: z.string().nullish(),
  Icon: z.string().nullish(),
  Description: z.string().nullish(),
  IsPinned: z.boolean().nullish(),
});

// ---------------------------------------------------------------------------
// Contact (search / property value)
// ---------------------------------------------------------------------------

export const ContactSearchFilterBodySchema = z.object({
  propertyDefinitionId: VoxaId,
  operator: z.number().int(),
  value: z.string(),
});

export const SearchContactsComplexBodySchema = z.object({
  customPropertyFilters: z.array(ContactSearchFilterBodySchema).optional(),
});

export const SetContactPropertyValueBodySchema = z.object({
  value: z.string(),
});

// ---------------------------------------------------------------------------
// Account API — service login response
// ---------------------------------------------------------------------------
// OAuth2 client_credentials grant response (POST /connect/token).
// Inferred type lives in voxa/types.ts.
export const HatifAccountTokenResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  token_type: z.string(),
});

// Channels API schemas + inferred types live in ./channels.ts so the file
// can stay byte-for-byte aligned with the Voxa-generated docs export.

// ---------------------------------------------------------------------------
// Tags — response
// ---------------------------------------------------------------------------
export const HatifTagSchema = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string().nullable(),
  isPinned: z.boolean(),
  creationTime: z.string(),
});

// ---------------------------------------------------------------------------
// Conversations — response
// ---------------------------------------------------------------------------
export const HatifChannelConversationSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  type: z.nativeEnum(ChannelType),
  icon: z.string().nullable(),
  phoneNumber: z.string().nullable(),
});

export const HatifConversationSchema = z.object({
  id: z.string(),
  sentiment: z.number().nullable(),
  status: z.nativeEnum(ConversationStatus),
  lastActivityAt: z.string(),
  contactId: z.string(),
  contactName: z.string().nullable(),
  contactType: z.number(),
  phoneNumber: z.string().nullable(),
  channelId: z.string().nullable(),
  assigneeId: z.string().nullable(),
  assignedAiAgentId: z.string().nullable(),
  isAiAssignee: z.boolean(),
  assigneeName: z.string().nullable(),
  channel: HatifChannelConversationSchema.nullable(),
  lastEventSourceType: z.nativeEnum(EventSourceType).nullable(),
  lastEventDirection: z.nativeEnum(EventDirection).nullable(),
  lastEventStatus: z.string().nullable(),
  lastEventPreviewBody: z.string().nullable(),
  lastEventOccurredAt: z.string().nullable(),
  lastEventsCount: z.number(),
  tags: z.array(HatifTagSchema),
  unreadCount: z.number(),
});

export const HatifAiSummarySchema = z.object({
  summary: z.string().nullable(),
  transcription: z.string().nullable(),
});

export const HatifReplyInfoSchema = z.object({
  repliedToEventId: z.string().nullable(),
  repliedToBody: z.string().nullable(),
});

export const HatifInternalThreadSchema = z.object({
  id: z.string(),
  isResolved: z.boolean(),
  messageCount: z.number(),
});

export const HatifLocationMessageSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  name: z.string().nullable(),
  address: z.string().nullable(),
});

// Voxa's actual wire shape for a conversation-handoff timeline event.
// Both "from" and "to" sides collapse user-or-AI into one `*AssigneeName`
// string for display.
export const HatifAssignationInfoSchema = z.object({
  fromUserId: z.string().nullable(),
  fromAiAgentId: z.string().nullable(),
  fromAssigneeName: z.string().nullable(),
  toUserId: z.string().nullable(),
  toAiAgentId: z.string().nullable(),
  toAssigneeName: z.string().nullable(),
});

// Rendered (already-sent) WhatsApp template content on an outbound template
// event. Unlike the VoxaTemplate *spec*, header/body/footer here are the final
// interpolated STRINGS, and buttons carry their resolved url/phone/copy code.
export const HatifSentTemplateButtonSchema = z.object({
  type: z.number(),
  text: z.string().nullable(),
  url: z.union([z.null(), z.string()]).optional(),
  phoneNumber: z.union([z.null(), z.string()]).optional(),
  copyCode: z.union([z.null(), z.string()]).optional(),
});
export type HatifSentTemplateButton = z.infer<
  typeof HatifSentTemplateButtonSchema
>;

export const HatifSentTemplateContentSchema = z.object({
  templateId: z.string().nullable().optional(),
  templateName: z.string().nullable().optional(),
  language: z.string().nullable().optional(),
  category: z.number().nullable().optional(),
  isExactContent: z.boolean().nullable().optional(),
  headerText: z.string().nullable().optional(),
  bodyText: z.string().nullable().optional(),
  footerText: z.string().nullable().optional(),
  headerMedia: z.union([z.null(), z.string()]).optional(),
  buttons: z
    .union([z.array(HatifSentTemplateButtonSchema), z.null()])
    .optional(),
});
export type HatifSentTemplateContent = z.infer<
  typeof HatifSentTemplateContentSchema
>;

export const HatifConversationTimelineEventSchema = z.object({
  eventId: z.string(),
  // WhatsApp message type (Voxa enum); 9 = template. Optional — older events
  // and non-WhatsApp sources omit it.
  messageType: z.number().nullable().optional(),
  // Present on outbound template sends — header/body/footer + buttons.
  sentTemplateContent: HatifSentTemplateContentSchema.nullable().optional(),
  conversationId: z.string(),
  contactId: z.string(),
  channelId: z.string(),
  sourceType: z.nativeEnum(EventSourceType),
  sourceId: z.string().nullable(),
  direction: z.nativeEnum(EventDirection),
  creationTime: z.string(),
  status: z.string(),
  errorCode: z.number().nullable(),
  failureReason: z.string().nullable(),
  isAi: z.boolean(),
  attachment: z.string().nullable(),
  ownerUserId: z.string().nullable(),
  aiAgentId: z.string().nullable(),
  durationOrSize: z.string().nullable(),
  ringingDuration: z.string().nullable(),
  body: z.string().nullable(),
  handlerName: z.string().nullable(),
  aiSummary: HatifAiSummarySchema.nullable(),
  replyTo: HatifReplyInfoSchema.nullable(),
  internalThread: HatifInternalThreadSchema.nullable(),
  location: HatifLocationMessageSchema.nullable(),
  assignation: HatifAssignationInfoSchema.nullable(),
});

export const HatifCreateConversationResultSchema = z.object({
  conversation: HatifConversationSchema,
  contactId: z.string(),
});

// ---------------------------------------------------------------------------
// WhatsApp send — response
// ---------------------------------------------------------------------------
// Both sendText and sendTemplate return the same shape — Voxa hands back
// the conversation event id so callers can correlate webhook updates.
export const HatifSendWhatsAppTextResultSchema = z.object({
  contactId: z.string(),
  status: z.string(),
  message: z.string(),
  conversationEventId: z.string(),
});

export const HatifSendWhatsAppTemplateResultSchema = z.object({
  contactId: z.string(),
  status: z.string(),
  message: z.string(),
  conversationEventId: z.string(),
});

// ═══════════════════════ (was notifications.ts) ═══════════════════════

// Notification subsystem — shared contracts. Single source of truth for the
// channel/event/audience surface used by BOTH the React frontend and the
// NestJS dispatcher. Pure types + as-const enums; no runtime side-effects.

// ---------------------------------------------------------------------------
// Channels / locales
// ---------------------------------------------------------------------------

export const NotificationChannel = {
  Sms: 'sms',
  Email: 'email',
  Whatsapp: 'whatsapp',
  Inapp: 'inapp',
} as const;
export type NotificationChannel =
  (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = [
  NotificationChannel.Sms,
  NotificationChannel.Email,
  NotificationChannel.Whatsapp,
  NotificationChannel.Inapp,
];

export const NotificationLocale = {
  Ar: 'ar',
  En: 'en',
} as const;
export type NotificationLocale =
  (typeof NotificationLocale)[keyof typeof NotificationLocale];

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export const NotificationEventKey = {
  InvoiceCreated: 'invoice.created',
  InvoiceSent: 'invoice.sent',
  InvoiceOverdue: 'invoice.overdue',
  InvoiceReminder: 'invoice.reminder',
  PaymentReceived: 'payment.received',
  PaymentRefunded: 'payment.refunded',
  ReceiptIssued: 'receipt.issued',

  ContractActivated: 'contract.activated',
  ContractExpired: 'contract.expired',
  ContractExpiringSoon: 'contract.expiringSoon',
  ContractRenewalRequested: 'contract.renewalRequested',
  ContractTerminationInitiated: 'contract.terminationInitiated',

  MaintenanceRequested: 'maintenance.requested',
  MaintenanceStatusChanged: 'maintenance.statusChanged',
  MaintenanceAssigned: 'maintenance.assigned',
  MaintenanceCompleted: 'maintenance.completed',

  TaskCreated: 'task.created',
  TaskAssigned: 'task.assigned',
  TaskReassigned: 'task.reassigned',
  TaskCompleted: 'task.completed',
  TaskDueSoon: 'task.dueSoon',

  InviteUser: 'invite.user',
  InviteAccepted: 'invite.accepted',
  InviteRejected: 'invite.rejected',
  InviteExpired: 'invite.expired',

  RegaComplianceFailed: 'rega.complianceFailed',
  AdLicenseExpiringSoon: 'adLicense.expiringSoon',
  ListingAdListed: 'listing.adListed',
  ListingVisitRequested: 'listing.visitRequested',
  ListingApplicationReceived: 'listing.applicationReceived',
  ListingAdExpired: 'listing.adExpired',

  UserVerified: 'user.verified',

  ServiceRequestCreated: 'serviceRequest.created',

  // A property owner invited to register via an employee's referral link. The
  // link (personal or collaborator) determines attribution; the message is the
  // same owner-facing invite, delivered as a WhatsApp template (referral slice).
  ReferralInvite: 'referral.invite',
} as const;
export type NotificationEventKey =
  (typeof NotificationEventKey)[keyof typeof NotificationEventKey];

// ---------------------------------------------------------------------------
// Audiences
// ---------------------------------------------------------------------------

export const NotificationAudience = {
  Tenant: 'tenant',
  Owner: 'owner',
  Admin: 'admin',
  Provider: 'provider',
  Employee: 'employee',
  Inviter: 'inviter',
  // The invitee. `InvitedUser` is the generic fallback; the per-portal-type
  // variants let invite.user carry role-tailored copy (the producer tags the
  // audience from invitations.role — see InviteService). They all resolve to
  // the same accept page (see url-builder `invitation_accept`).
  InvitedUser: 'invited_user',
  InvitedOwner: 'invited_owner',
  InvitedTenant: 'invited_tenant',
  InvitedEmployee: 'invited_employee',
  InvitedServiceProvider: 'invited_service_provider',
  // Static-per-company recipient (e.g. finance@dallali.com). Looked up via
  // company_notification_recipients at dispatch time. Not a user role; no
  // per-user prefs apply, super-admin channel config still gates delivery.
  Finance: 'finance',
} as const;
export type NotificationAudience =
  (typeof NotificationAudience)[keyof typeof NotificationAudience];

// The invitee audiences (generic + per-portal-type). url-builder and any
// invite-specific logic treat the whole family as "the person being invited".
export const INVITED_AUDIENCES: readonly NotificationAudience[] = [
  NotificationAudience.InvitedUser,
  NotificationAudience.InvitedOwner,
  NotificationAudience.InvitedTenant,
  NotificationAudience.InvitedEmployee,
  NotificationAudience.InvitedServiceProvider,
];

// Single source of truth for mapping an invited party's role / profile type
// (invitations.role / app_role) to the per-portal invite audience. The four
// portal types have tailored invite templates; staff roles
// (admin/manager/super_admin) and anything unknown fall back to the generic
// invited_user copy. Used by BOTH the frontend invite modal and the backend
// InviteService so the two never drift.
export const ROLE_TO_INVITED_AUDIENCE: Readonly<
  Record<string, NotificationAudience>
> = {
  owner: NotificationAudience.InvitedOwner,
  tenant: NotificationAudience.InvitedTenant,
  employee: NotificationAudience.InvitedEmployee,
  service_provider: NotificationAudience.InvitedServiceProvider,
};

export function invitedAudienceFor(role?: string | null): NotificationAudience {
  return (
    (role ? ROLE_TO_INVITED_AUDIENCE[role] : undefined) ??
    NotificationAudience.InvitedUser
  );
}

// Maps a user's profile type (user_roles.role) to the audience they receive
// notifications as. Mirrors the SQL fan-out: admin / super_admin / self_admin
// all resolve to Admin, matching get_company_admin_user_ids. `manager` is kept
// defensively — the value survives in the app_role enum, but user_roles carries
// a CHECK constraint (user_roles_role_not_manager) making it unreachable.
export const ROLE_TO_AUDIENCE: Readonly<Record<string, NotificationAudience>> =
  {
    super_admin: NotificationAudience.Admin,
    admin: NotificationAudience.Admin,
    self_admin: NotificationAudience.Admin,
    manager: NotificationAudience.Admin,
    employee: NotificationAudience.Employee,
    owner: NotificationAudience.Owner,
    tenant: NotificationAudience.Tenant,
    service_provider: NotificationAudience.Provider,
  };

// `Inviter` is not a profile type — it's "the person who sent this invite" —
// so it can't come from ROLE_TO_AUDIENCE. Only staff create `invitations` rows
// (the invite dialogs are mounted behind settings.view).
const INVITER_ROLES: readonly string[] = ['super_admin', 'admin', 'self_admin'];

// Every audience a user receives as, given the full set of roles they hold. A
// user who is both Owner and Admin gets the union — which is the only honest
// view, because notification_preferences rows are keyed by user_id alone (no
// role, no company), so one row means the same thing in every portal.
//
// NEVER returns Finance: that audience is a per-company address in
// company_notification_recipients, not a user. The admin settings matrix still
// renders Finance as a row — opposite rules on the same enum value, deliberately.
export function audiencesForRoles(
  roles: readonly string[]
): NotificationAudience[] {
  const out = new Set<NotificationAudience>();
  for (const role of roles) {
    const audience = ROLE_TO_AUDIENCE[role];
    if (audience) out.add(audience);
  }
  if (roles.some((r) => INVITER_ROLES.includes(r))) {
    out.add(NotificationAudience.Inviter);
  }
  return [...out];
}

// ---------------------------------------------------------------------------
// Send status / skip reasons
// ---------------------------------------------------------------------------

export const NotificationSendStatus = {
  Pending: 'pending',
  Sent: 'sent',
  Delivered: 'delivered',
  Failed: 'failed',
  Skipped: 'skipped',
  // Transient claim state used by the retry cron: a failed send it has picked
  // up and is re-driving. Never a terminal state — flips back to sent/failed.
  Retrying: 'retrying',
} as const;
export type NotificationSendStatus =
  (typeof NotificationSendStatus)[keyof typeof NotificationSendStatus];

export const NotificationSkipReason = {
  ChannelDisabledByPlatform: 'channel_disabled_by_platform',
  ChannelDisabledByUser: 'channel_disabled_by_user',
  EventDisabledByCompany: 'event_disabled_by_company',
  // The company admin turned this (event, audience, channel) off in
  // notification_audience_config — e.g. "employees don't get WhatsApp for
  // payment.received". Distinct from ChannelDisabledByPlatform, which means
  // the company never enabled the channel at all.
  AudienceDisabledByCompany: 'audience_disabled_by_company',
  MissingRecipientContact: 'missing_recipient_contact',
  NoChannelsEligible: 'no_channels_eligible',
  TemplateMissing: 'template_missing',
  // WhatsApp specific: recipient is outside the 24h customer service
  // window and no approved template was registered. Voxa refuses the
  // sendText with HTTP 403 + code ServiceWindowExpired.
  OutsideSessionWindow: 'outside_session_window',
  // An identical send (same event instance + audience + channel + recipient,
  // within a short window) was already recorded — the dispatcher short-circuits
  // on the notification_sends dedupe_key unique index so a double-fire (cron
  // overlap / pg_net retry) can't produce a duplicate provider call (and a
  // duplicate WhatsApp 409).
  Duplicate: 'duplicate',
} as const;
export type NotificationSkipReason =
  (typeof NotificationSkipReason)[keyof typeof NotificationSkipReason];

// Default-channel policy: when the user has NO row in notification_preferences
// for a given (event, channel), enabled=true iff the channel is in this set.
//
// All four channels default ON at the USER level. Per-company gating
// (company_available_channels + company_channel_settings, where sms + whatsapp
// default OFF) is applied AFTER user prefs in the dispatcher, so a user who
// leaves SMS on but whose company hasn't enabled SMS still won't receive one.
// The intent is to let the user opt out of channels rather than opt in.
export const DEFAULT_ENABLED_CHANNELS: readonly NotificationChannel[] = [
  NotificationChannel.Sms,
  NotificationChannel.Email,
  NotificationChannel.Whatsapp,
  NotificationChannel.Inapp,
];

// ---------------------------------------------------------------------------
// Event-meta (UI grouping + labels)
// ---------------------------------------------------------------------------

export const NotificationDomain = {
  Payments: 'payments',
  Contracts: 'contracts',
  Maintenance: 'maintenance',
  Tasks: 'tasks',
  Invitations: 'invitations',
  Listings: 'listings',
  Auth: 'auth',
} as const;
export type NotificationDomain =
  (typeof NotificationDomain)[keyof typeof NotificationDomain];

export const NotificationEventMode = {
  Auto: 'auto',
  Manual: 'manual',
} as const;
export type NotificationEventMode =
  (typeof NotificationEventMode)[keyof typeof NotificationEventMode];

export interface NotificationEventMetaEntry {
  domain: NotificationDomain;
  mode: NotificationEventMode;
  // The set of audiences this event renders for; the dispatcher uses the
  // metadata.audience from the incoming row to pick the right template
  // when the event was emitted from a DB trigger (which writes one row
  // per audience). For explicit Nest calls, the caller passes the
  // audience directly.
  audiences: readonly NotificationAudience[];
  // Bilingual labels for the prefs UI (optional for callers that only need
  // routing). Frontend renders these; backend ignores them.
  labelAr?: string;
  labelEn?: string;
}

// ---------------------------------------------------------------------------
// Recipient + rendered payloads
// ---------------------------------------------------------------------------

export interface NotificationRecipient {
  userId?: string;
  name: string;
  phone?: string;
  email?: string;
  whatsappTo?: string;
  locale?: NotificationLocale;
}

export interface RenderedSms {
  body: string;
}

export interface RenderedEmail {
  subject: string;
  bodyText: string;
  bodyHtml?: string;
}

export interface RenderedWhatsapp {
  // Used when no approved template exists yet (inside-24h window only).
  text?: string;
  // Used for outside-24h-window outbound — Voxa sendTemplate.
  templateName?: string;
  language?: NotificationLocale;
  parameters?: HatifWhatsAppTemplateParameter[];
}

export interface RenderedInapp {
  title: string;
  message: string;
  link?: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  category?: string;
}

// What an adapter returns after attempting a send.
export interface ChannelSendResult {
  externalId?: string;
  providerMetadata?: Record<string, string | number | boolean | null>;
}

// ---------------------------------------------------------------------------
// Dispatch request / response (frontend → /notifications/dispatch)
// ---------------------------------------------------------------------------

export interface NotificationDispatchRequest {
  eventKey: NotificationEventKey;
  companyId: string;
  audience: NotificationAudience;
  recipient: NotificationRecipient;
  context?: Record<string, unknown>;
  // Optional: caller-forced channel set (used by manual events like
  // invite.user / invoice.sent). When omitted the dispatcher reads user prefs.
  channels?: NotificationChannel[];
  // Channels to drop AFTER resolution (e.g. the trigger-driven path skips
  // Inapp because the row already exists).
  excludeChannels?: NotificationChannel[];
  sentBy?: string;
}

export interface NotificationDispatchResponse {
  eventKey: NotificationEventKey;
  results: Array<{
    channel: NotificationChannel;
    status: NotificationSendStatus;
    externalId?: string;
    skipReason?: NotificationSkipReason;
    errorMessage?: string;
  }>;
}
export const NOTIFICATION_EVENT_META: Record<
  NotificationEventKey,
  NotificationEventMetaEntry
> = {
  // Payment / invoice events: emitted with concrete audience (tenant or
  // owner) by the trigger based on invoices.recipient_role. Finance fans
  // out secondarily.
  [NotificationEventKey.InvoiceCreated]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Finance,
    ],
    labelAr: 'إنشاء فاتورة جديدة',
    labelEn: 'New invoice created',
  },
  [NotificationEventKey.InvoiceSent]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Manual,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Finance,
    ],
    labelAr: 'إرسال فاتورة',
    labelEn: 'Invoice sent',
  },
  [NotificationEventKey.InvoiceOverdue]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
      NotificationAudience.Finance,
    ],
    labelAr: 'فاتورة متأخرة',
    labelEn: 'Invoice overdue',
  },
  [NotificationEventKey.InvoiceReminder]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Finance,
    ],
    labelAr: 'تذكير بفاتورة',
    labelEn: 'Invoice reminder',
  },
  [NotificationEventKey.PaymentReceived]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
      NotificationAudience.Finance,
    ],
    labelAr: 'استلام دفعة',
    labelEn: 'Payment received',
  },
  [NotificationEventKey.PaymentRefunded]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Finance,
    ],
    labelAr: 'استرداد دفعة',
    labelEn: 'Payment refunded',
  },
  [NotificationEventKey.ReceiptIssued]: {
    domain: NotificationDomain.Payments,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Finance,
    ],
    labelAr: 'إصدار سند قبض',
    labelEn: 'Receipt issued',
  },

  [NotificationEventKey.ContractActivated]: {
    domain: NotificationDomain.Contracts,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
    ],
    labelAr: 'تفعيل عقد',
    labelEn: 'Contract activated',
  },
  [NotificationEventKey.ContractExpired]: {
    domain: NotificationDomain.Contracts,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
    ],
    labelAr: 'انتهاء عقد',
    labelEn: 'Contract expired',
  },
  [NotificationEventKey.ContractExpiringSoon]: {
    domain: NotificationDomain.Contracts,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
    ],
    labelAr: 'عقد على وشك الانتهاء',
    labelEn: 'Contract expiring soon',
  },
  [NotificationEventKey.ContractRenewalRequested]: {
    domain: NotificationDomain.Contracts,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Owner, NotificationAudience.Admin],
    labelAr: 'طلب تجديد عقد',
    labelEn: 'Contract renewal requested',
  },
  [NotificationEventKey.ContractTerminationInitiated]: {
    domain: NotificationDomain.Contracts,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
    ],
    labelAr: 'بدء إنهاء عقد',
    labelEn: 'Contract termination initiated',
  },

  [NotificationEventKey.MaintenanceRequested]: {
    domain: NotificationDomain.Maintenance,
    mode: NotificationEventMode.Auto,
    // admin acts on it; tenant gets a "received" confirmation.
    audiences: [NotificationAudience.Admin, NotificationAudience.Tenant],
    labelAr: 'طلب صيانة جديد',
    labelEn: 'Maintenance requested',
  },
  [NotificationEventKey.MaintenanceStatusChanged]: {
    domain: NotificationDomain.Maintenance,
    mode: NotificationEventMode.Auto,
    // tenant only — owner hears the assigned + completed milestones instead.
    audiences: [NotificationAudience.Tenant],
    labelAr: 'تغير حالة الصيانة',
    labelEn: 'Maintenance status changed',
  },
  [NotificationEventKey.MaintenanceAssigned]: {
    domain: NotificationDomain.Maintenance,
    mode: NotificationEventMode.Auto,
    // provider gets the job, tenant who's coming, owner "work scheduled".
    // NOT employee: maintenance_assignments links only to service_providers —
    // there is no employee assignment on maintenance anywhere in the schema, so
    // declaring an employee audience here would be unemittable.
    audiences: [
      NotificationAudience.Provider,
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
    ],
    labelAr: 'إسناد طلب صيانة',
    labelEn: 'Maintenance assigned',
  },
  [NotificationEventKey.MaintenanceCompleted]: {
    domain: NotificationDomain.Maintenance,
    mode: NotificationEventMode.Auto,
    audiences: [
      NotificationAudience.Tenant,
      NotificationAudience.Owner,
      NotificationAudience.Admin,
    ],
    labelAr: 'إكمال طلب صيانة',
    labelEn: 'Maintenance completed',
  },

  [NotificationEventKey.TaskCreated]: {
    domain: NotificationDomain.Tasks,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin],
    labelAr: 'إنشاء مهمة',
    labelEn: 'Task created',
  },
  [NotificationEventKey.TaskAssigned]: {
    domain: NotificationDomain.Tasks,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Employee],
    labelAr: 'إسناد مهمة',
    labelEn: 'Task assigned',
  },
  [NotificationEventKey.TaskReassigned]: {
    domain: NotificationDomain.Tasks,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Employee],
    labelAr: 'إعادة إسناد مهمة',
    labelEn: 'Task reassigned',
  },
  [NotificationEventKey.TaskCompleted]: {
    domain: NotificationDomain.Tasks,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin],
    labelAr: 'إكمال مهمة',
    labelEn: 'Task completed',
  },
  [NotificationEventKey.TaskDueSoon]: {
    domain: NotificationDomain.Tasks,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Employee],
    labelAr: 'مهمة تستحق قريباً',
    labelEn: 'Task due soon',
  },

  [NotificationEventKey.InviteUser]: {
    domain: NotificationDomain.Invitations,
    mode: NotificationEventMode.Manual,
    // InvitedUser stays first — it's the generic fallback the dispatch-from-row
    // path uses when no audience is stamped. The per-portal-type variants carry
    // role-tailored copy (InviteService picks one from invitations.role).
    audiences: [
      NotificationAudience.InvitedUser,
      NotificationAudience.InvitedOwner,
      NotificationAudience.InvitedTenant,
      NotificationAudience.InvitedEmployee,
      NotificationAudience.InvitedServiceProvider,
    ],
    labelAr: 'دعوة مستخدم',
    labelEn: 'Invite user',
  },
  [NotificationEventKey.InviteAccepted]: {
    domain: NotificationDomain.Invitations,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Inviter],
    labelAr: 'قبول دعوة',
    labelEn: 'Invitation accepted',
  },
  [NotificationEventKey.InviteRejected]: {
    domain: NotificationDomain.Invitations,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Inviter],
    labelAr: 'رفض دعوة',
    labelEn: 'Invitation rejected',
  },
  [NotificationEventKey.InviteExpired]: {
    domain: NotificationDomain.Invitations,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Inviter],
    labelAr: 'انتهاء صلاحية دعوة',
    labelEn: 'Invitation expired',
  },

  [NotificationEventKey.RegaComplianceFailed]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Owner, NotificationAudience.Admin],
    labelAr: 'فشل التحقق من رخصة الإعلان',
    labelEn: 'REGA compliance failed',
  },
  [NotificationEventKey.AdLicenseExpiringSoon]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Owner, NotificationAudience.Admin],
    labelAr: 'رخصة إعلان على وشك الانتهاء',
    labelEn: 'Ad license expiring soon',
  },
  [NotificationEventKey.ListingAdListed]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin],
    labelAr: 'إعلان جديد منشور',
    labelEn: 'New ad listed',
  },
  [NotificationEventKey.ListingVisitRequested]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin, NotificationAudience.Employee],
    labelAr: 'طلب معاينة جديد',
    labelEn: 'New visit request',
  },
  [NotificationEventKey.ListingApplicationReceived]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin, NotificationAudience.Employee],
    labelAr: 'طلب جديد على إعلان',
    labelEn: 'New application received',
  },
  [NotificationEventKey.ListingAdExpired]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin, NotificationAudience.Employee],
    labelAr: 'انتهاء إعلان',
    labelEn: 'Ad expired',
  },

  // Auth / verification — admin learns when a user passes Nafath.
  [NotificationEventKey.UserVerified]: {
    domain: NotificationDomain.Auth,
    mode: NotificationEventMode.Auto,
    audiences: [NotificationAudience.Admin],
    labelAr: 'تم التحقق من مستخدم',
    labelEn: 'User verified',
  },
  [NotificationEventKey.ServiceRequestCreated]: {
    domain: NotificationDomain.Listings,
    mode: NotificationEventMode.Auto,
    // Admin/manager only — the producer (notify_new_service_request trigger)
    // emits to admins, and there is no employee /requests route. Employee was
    // declared here but never wired (no emit, no deep-link), so it's dropped.
    audiences: [NotificationAudience.Admin],
    labelAr: 'طلب خدمة جديد',
    labelEn: 'New service request',
  },
  // Manually triggered from the employee "Referral Links" page — the employee
  // sends a prospective owner a WhatsApp invite carrying their referral code.
  [NotificationEventKey.ReferralInvite]: {
    domain: NotificationDomain.Invitations,
    mode: NotificationEventMode.Manual,
    audiences: [NotificationAudience.InvitedOwner],
    labelAr: 'دعوة إحالة',
    labelEn: 'Referral invite',
  },
};

// ---------------------------------------------------------------------------
// Audience × channel defaults
// ---------------------------------------------------------------------------
//
// Answers "who hears about this event, and how" — the WHO half of delivery.
// Composes multiplicatively with company_available_channels(), which answers
// the separate question of whether the company enabled that channel at all
// (sms + whatsapp default OFF there). A channel enabled here is still silent
// if the company never turned it on.
//
// These values are seeded as the platform-default tier of
// notification_audience_config (company_id IS NULL). Company admins override
// individual cells; anything they never touch resolves through to here, so
// changing a default here changes behaviour for every company that hasn't
// opted out — no backfill, by design.

// Staff live in the dashboard, so routine work reaches them there. SMS and
// WhatsApp are reserved for things that are genuinely interruptive.
const STAFF_DEFAULT_CHANNELS: readonly NotificationChannel[] = [
  NotificationChannel.Email,
  NotificationChannel.Inapp,
];

// The bell only — for activity an admin wants a record of but shouldn't be
// paged about.
const INAPP_ONLY: readonly NotificationChannel[] = [NotificationChannel.Inapp];

// Regulatory deadlines: missing one costs a REGA fine, so these earn WhatsApp.
const ADMIN_URGENT_CHANNELS: readonly NotificationChannel[] = [
  NotificationChannel.Email,
  NotificationChannel.Whatsapp,
  NotificationChannel.Inapp,
];

// The one case where an employee SHOULD be pushed: the work is assigned to
// them personally. Everything else about company-wide activity stays silent.
const ASSIGNED_TO_ME_CHANNELS: readonly NotificationChannel[] = [
  NotificationChannel.Inapp,
  NotificationChannel.Email,
  NotificationChannel.Whatsapp,
];

// Per-audience-class baseline, applied to any (event, audience) pair the event
// catalog declares and AUDIENCE_DEFAULT_OVERRIDES doesn't override.
export const DEFAULT_AUDIENCE_CHANNELS: Readonly<
  Record<NotificationAudience, readonly NotificationChannel[]>
> = {
  // External parties have no dashboard — reach them however we can.
  [NotificationAudience.Tenant]: NOTIFICATION_CHANNELS,
  [NotificationAudience.Owner]: NOTIFICATION_CHANNELS,
  [NotificationAudience.Provider]: NOTIFICATION_CHANNELS,
  [NotificationAudience.InvitedUser]: NOTIFICATION_CHANNELS,
  [NotificationAudience.InvitedOwner]: NOTIFICATION_CHANNELS,
  [NotificationAudience.InvitedTenant]: NOTIFICATION_CHANNELS,
  [NotificationAudience.InvitedEmployee]: NOTIFICATION_CHANNELS,
  [NotificationAudience.InvitedServiceProvider]: NOTIFICATION_CHANNELS,
  [NotificationAudience.Finance]: NOTIFICATION_CHANNELS,
  [NotificationAudience.Admin]: STAFF_DEFAULT_CHANNELS,
  [NotificationAudience.Inviter]: STAFF_DEFAULT_CHANNELS,
  // Silent by default. Re-opened per event below, only for assignments.
  [NotificationAudience.Employee]: [],
};

// Per-event deltas. An entry replaces the class default for that audience; an
// empty array means "off on every channel". Only non-redundant entries appear —
// an audience the event catalog doesn't declare already resolves to [].
export const AUDIENCE_DEFAULT_OVERRIDES: Readonly<
  Partial<
    Record<
      NotificationEventKey,
      Partial<Record<NotificationAudience, readonly NotificationChannel[]>>
    >
  >
> = {
  // ── Payments: the headline complaint. Admins keep a record, not an inbox. ──
  [NotificationEventKey.PaymentReceived]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },
  // invoice.overdue keeps email — money is at risk.

  // ── Contracts: activation/expiry are records; deadlines stay email. ────────
  [NotificationEventKey.ContractActivated]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },
  [NotificationEventKey.ContractExpired]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },

  // ── Maintenance ───────────────────────────────────────────────────────────
  [NotificationEventKey.MaintenanceCompleted]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },

  // ── Tasks: assignment is the entire point of notifying an employee. ───────
  [NotificationEventKey.TaskAssigned]: {
    [NotificationAudience.Employee]: ASSIGNED_TO_ME_CHANNELS,
  },
  [NotificationEventKey.TaskReassigned]: {
    [NotificationAudience.Employee]: ASSIGNED_TO_ME_CHANNELS,
  },
  [NotificationEventKey.TaskDueSoon]: {
    [NotificationAudience.Employee]: ASSIGNED_TO_ME_CHANNELS,
  },
  [NotificationEventKey.TaskCreated]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },
  [NotificationEventKey.TaskCompleted]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },

  // ── Listings ──────────────────────────────────────────────────────────────
  [NotificationEventKey.ListingAdListed]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },
  [NotificationEventKey.ListingAdExpired]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
    // Informational for the employee assigned to the property, not an action.
    [NotificationAudience.Employee]: INAPP_ONLY,
  },
  // Leads on a property this employee is assigned to.
  //
  // These are tuned from PRODUCTION volume, not from first principles:
  // listing.visitRequested alone is 43% of one company's entire notification
  // output (449 in-app rows + ~1,000 external sends in 30 days, to 6 admins and
  // 6 employees). Treating it like a task assignment — the earlier default —
  // left the single largest source of the reported noise untouched.
  //
  // So: the assigned employee owns the lead and keeps the fastest channel
  // (WhatsApp) plus the in-app record; admins drop to the bell only, because
  // chasing every viewing request on every property is the employee's job, not
  // a thing all six admins need pushed to them. Email is dropped on both sides —
  // for a same-day lead it is the least useful channel and was the bulkiest.
  [NotificationEventKey.ListingVisitRequested]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
    [NotificationAudience.Employee]: [
      NotificationChannel.Inapp,
      NotificationChannel.Whatsapp,
    ],
  },
  [NotificationEventKey.ListingApplicationReceived]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
    [NotificationAudience.Employee]: [
      NotificationChannel.Inapp,
      NotificationChannel.Whatsapp,
    ],
  },
  [NotificationEventKey.RegaComplianceFailed]: {
    [NotificationAudience.Admin]: ADMIN_URGENT_CHANNELS,
  },
  [NotificationEventKey.AdLicenseExpiringSoon]: {
    [NotificationAudience.Admin]: ADMIN_URGENT_CHANNELS,
  },

  // ── Auth ──────────────────────────────────────────────────────────────────
  [NotificationEventKey.UserVerified]: {
    [NotificationAudience.Admin]: INAPP_ONLY,
  },
};

// The default channel set for one (event, audience) pair. This is where
// NOTIFICATION_EVENT_META.audiences stops being documentation and becomes
// enforcement: an audience the event doesn't declare gets nothing.
export function defaultAudienceChannels(
  eventKey: NotificationEventKey,
  audience: NotificationAudience
): readonly NotificationChannel[] {
  const override = AUDIENCE_DEFAULT_OVERRIDES[eventKey]?.[audience];
  if (override) return override;
  if (!NOTIFICATION_EVENT_META[eventKey].audiences.includes(audience))
    return [];
  return DEFAULT_AUDIENCE_CHANNELS[audience];
}

export function isAudienceChannelDefaultEnabled(
  eventKey: NotificationEventKey,
  audience: NotificationAudience,
  channel: NotificationChannel
): boolean {
  return defaultAudienceChannels(eventKey, audience).includes(channel);
}

export interface NotificationAudienceDefaultRow {
  eventKey: NotificationEventKey;
  audience: NotificationAudience;
  channel: NotificationChannel;
  enabled: boolean;
}

// The full dense platform tier: every event × audience × channel. Feeds BOTH
// the migration's seed generator and the test:db completeness assertion, so the
// seeded rows can never silently drift from this file.
export function notificationAudienceDefaultMatrix(): readonly NotificationAudienceDefaultRow[] {
  const rows: NotificationAudienceDefaultRow[] = [];
  for (const eventKey of notificationEventKeys()) {
    for (const audience of Object.values(NotificationAudience)) {
      const channels = defaultAudienceChannels(eventKey, audience);
      for (const channel of NOTIFICATION_CHANNELS) {
        rows.push({
          eventKey,
          audience,
          channel,
          enabled: channels.includes(channel),
        });
      }
    }
  }
  return rows;
}

// Type guard for runtime channel validation (e.g. parsing API payloads).
export function isNotificationChannel(
  value: string
): value is NotificationChannel {
  return (NOTIFICATION_CHANNELS as readonly string[]).includes(value);
}

// Type guard for runtime event-key validation (e.g. parsing trigger metadata).
export function isNotificationEventKey(
  value: string
): value is NotificationEventKey {
  return value in NOTIFICATION_EVENT_META;
}

// Type guard for runtime audience validation (e.g. parsing trigger metadata).
export function isNotificationAudience(
  value: string
): value is NotificationAudience {
  for (const a of Object.values(NotificationAudience)) {
    if (a === value) return true;
  }
  return false;
}

// Typed view over Object.keys(NOTIFICATION_EVENT_META) — `Object.keys` widens
// to `string[]`, so callers would otherwise need an `as NotificationEventKey[]`
// cast. Centralising it here keeps the cast in one place and matches the
// actual key set of the meta table.
export function notificationEventKeys(): readonly NotificationEventKey[] {
  return Object.keys(
    NOTIFICATION_EVENT_META
  ) as readonly NotificationEventKey[];
}
